import React, { useState, useRef } from 'react';
import { Modal } from '../ui/Modal';
import { Upload, Plus, Loader2, AlertTriangle } from 'lucide-react';
import { samGovApi } from '../../lib/api/samGov';
import { toast } from 'react-hot-toast';

interface AddContractModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContractAdded: () => void;
}

interface ContractForm {
  noticeId: string;
  title: string;
  agency: string;
  description: string;
  value: string;
  deadline: string;
  naicsCode: string;
  setAside: string;
}

const initialForm: ContractForm = {
  noticeId: '',
  title: '',
  agency: '',
  description: '',
  value: '',
  deadline: '',
  naicsCode: '',
  setAside: '',
};

export function AddContractModal({ isOpen, onClose, onContractAdded }: AddContractModalProps) {
  const [form, setForm] = useState<ContractForm>(initialForm);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Fetch from SAM.gov if notice ID is provided
      if (form.noticeId) {
        const [contract] = await samGovApi.fetchContracts({ noticeId: form.noticeId });
        if (!contract) {
          throw new Error('Contract not found on SAM.gov');
        }
        onContractAdded();
        toast.success('Contract imported successfully from SAM.gov');
      } else {
        // Handle manual contract creation
        // This would typically involve saving to your database
        toast.success('Contract added successfully');
      }
      onClose();
      setForm(initialForm);
    } catch (error) {
      console.error('Failed to add contract:', error);
      toast.error('Failed to add contract');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;

    setImporting(true);
    try {
      const importedContracts = await samGovApi.importContracts(files[0]);
      toast.success(`Successfully imported ${importedContracts.length} contracts`);
      onContractAdded();
      onClose();
    } catch (error) {
      console.error('Import failed:', error);
      toast.error('Failed to import contracts');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Contract">
      <div className="space-y-6">
        {/* File Upload Section */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Import from File
          </label>
          <div className="mt-1">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept=".csv,.xlsx,.pdf"
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="flex w-full items-center justify-center rounded-lg border-2 border-dashed border-gray-300 px-6 py-4 hover:border-gray-400"
            >
              {importing ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-5 w-5" />
                  Upload CSV, Excel, or PDF file
                </>
              )}
            </button>
            <p className="mt-1 text-xs text-gray-500">
              Supported formats: CSV, XLSX, PDF
            </p>
          </div>
        </div>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white px-2 text-sm text-gray-500">or</span>
          </div>
        </div>

        {/* SAM.gov Import Section */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Import from SAM.gov
          </label>
          <div className="mt-1">
            <input
              type="text"
              value={form.noticeId}
              onChange={(e) => setForm({ ...form, noticeId: e.target.value })}
              placeholder="Enter SAM.gov Notice ID"
              className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
            />
          </div>
        </div>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white px-2 text-sm text-gray-500">or</span>
          </div>
        </div>

        {/* Manual Entry Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                Contract Title
              </label>
              <input
                type="text"
                id="title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
              />
            </div>

            <div>
              <label htmlFor="agency" className="block text-sm font-medium text-gray-700">
                Agency
              </label>
              <input
                type="text"
                id="agency"
                value={form.agency}
                onChange={(e) => setForm({ ...form, agency: e.target.value })}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
              />
            </div>

            <div>
              <label htmlFor="value" className="block text-sm font-medium text-gray-700">
                Contract Value
              </label>
              <input
                type="number"
                id="value"
                value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
              />
            </div>

            <div>
              <label htmlFor="deadline" className="block text-sm font-medium text-gray-700">
                Deadline
              </label>
              <input
                type="date"
                id="deadline"
                value={form.deadline}
                onChange={(e) => setForm({ ...form, deadline: e.target.value })}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
              />
            </div>

            <div>
              <label htmlFor="naicsCode" className="block text-sm font-medium text-gray-700">
                NAICS Code
              </label>
              <input
                type="text"
                id="naicsCode"
                value={form.naicsCode}
                onChange={(e) => setForm({ ...form, naicsCode: e.target.value })}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
              />
            </div>

            <div>
              <label htmlFor="setAside" className="block text-sm font-medium text-gray-700">
                Set-Aside
              </label>
              <select
                id="setAside"
                value={form.setAside}
                onChange={(e) => setForm({ ...form, setAside: e.target.value })}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
              >
                <option value="">None</option>
                <option value="8(a)">8(a)</option>
                <option value="HUBZone">HUBZone</option>
                <option value="SDVOSB">SDVOSB</option>
                <option value="WOSB">WOSB</option>
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              id="description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={4}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
            />
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Contract
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}